
import S3PhotoModel from './S3PhotoModel';
import LocationModel from './LocationModel';

export default class UserModel {
  constructor(data) {
    if (!data) {
      data = {};
    }

    this.id = data.id || 0;
    this.first_name = data.first_name || '';
    this.last_name = data.last_name || '';
    this.date_of_birth = data.date_of_birth || '';
    this.gender = this.formatProperty(data.gender);
    this.race = this.formatProperty(data.race);
    this.bio = data.bio || '';
    this.email = data.email || '';
    this.phone_number = data.phone_number || '';
    this.school = data.school || null;
    this.city = data.city ? new LocationModel(data.city) : null;
    this.photo = data.photo ? new S3PhotoModel(data.photo) : null;

    this.num_pending_payments = 0;
    this.allow_sms_notifications = data.allow_sms_notifications || false;
    this.unread_messages_count = data.unread_messages_count || 0;

    this.determineUserRole(data);

    this.student_profile = null; // filled via 2nd api call
    this.counselor_profile = null;
  }

  formatProperty(value) {
    return value === 'n/a' ? 'N/A' : value || '';
  }

  determineUserRole(data) {
    this.is_counselor = data.is_counselor || false;
    this.is_student = data.is_student || false;
    this.is_college_admin = data.is_college_admin || false;
    this.is_super_admin = data.is_super_admin || false;
    this.isClinicalOpsAdmin = false;

    switch (data.role) {
      case 'user':
        this.is_student = true;
        break;
      case 'counselor':
        this.is_counselor = true;
        break;
      case 'institution-admin':
        this.is_college_admin = true;
        break;
      case 'bettermynd-clinicalops':
        this.isClinicalOpsAdmin = true;
        this.is_super_admin = true;
        break;
      case 'bettermynd-admin':
        this.is_super_admin = true;
        break;
      default:
        break;
    }
  }

  get display_label() {
    if (this.email) {
      return `${this.displayName} (${this.email})`;
    }

    return this.displayName;
  }

  get userTypeDisplay() {
    if (this.is_super_admin) {
      return 'Admin';
    }

    if (this.is_college_admin) {
      return 'Admin';
    }

    if (this.is_counselor) {
      return 'Counselor';
    }

    if (this.is_student) {
      return 'User';
    }

    return '';
  }

  get isStudent() {
    return this.is_student;
  }

  get isCounselor() {
    return this.is_counselor;
  }

  get isAdmin() {
    return this.is_super_admin;
  }

  get isCollegeAdmin() {
    return this.is_college_admin;
  }

  get isClinicalOps() {
    return this.isClinicalOpsAdmin;
  }

  get canAddEvents() {
    return this.isAdmin || this.isCollegeAdmin;
  }

  get canAddSessions() {
    return this.isAdmin || this.isCollegeAdmin || this.isCounselor;
  }

  // Login/Register -> Confirm -> Welcome -> Survey

  get needsWelcome() {
    if (this.isCounselor) {
      return !this.counselor_profile
        || !this.counselor_profile.completed_profile;
    }

    if (this.isStudent) {
      return !this.student_profile.completed_survey;
    }

    return false;
  }

  get hasInitializedCal() {
    return this.counselor_profile && (this.counselor_profile.calendar_initialized || this.counselor_profile.in_app_calendar_initialized);
  }

  get needsSurvey() {
    return this.isStudent && (!this.student_profile || !this.student_profile.completed_survey);
  }

  get homeRouteName() {
    if (this.isStudent) {
      return this.getStudentRoute();
    }

    if (this.isCounselor) {
      return this.getCounselorRoute();
    }

    if (this.isCollegeAdmin) {
      return 'college-dashboard';
    }

    if (this.isAdmin) {
      return 'admin-dashboard';
    }

    return 'dashboard';
  }

  getStudentRoute() {
    return this.needsWelcome ? 'welcome' : 'student-dashboard';
  }

  getCounselorRoute() {
    if (this.needsWelcome) return 'counselor-welcome';
    if (!this.hasInitializedCal) return 'availability';
    return 'counselor-dashboard';
  }

  get displayName() {
    return this.first_name || (this.isStudent ? 'Student' : 'Counselor');
  }

  get fullNameShort() {
    if (this.last_name && this.last_name.length) {
      return `${this.first_name} ${this.last_name[0]}`;
    }
    return this.displayName;
  }

  get fullName() {
    return `${this.first_name} ${this.last_name}`;
  }

  // welcome step 2
  get student_status() {
    return this.student_profile && this.student_profile.student_status;
  }

  get international_student() {
    return this.student_profile && this.student_profile.international_student;
  }

  get student_athlete() {
    return this.student_profile && this.student_profile.student_athlete;
  }

  storeWelcomeStepOneData(user) {
    this.date_of_birth = user.date_of_birth;
    this.gender = user.gender;
    this.race = user.race;
    this.city = user.city;
  }

  setFinishedCounselorOnboarding() {
    this.counselor_profile.completed_profile = true;
    this.counselor_profile.calendar_initialized = true;
  }
}
